import "@/locales/registerPlanChangeI18n";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { Loader2 } from "lucide-react";
import posthog from "posthog-js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { AppRouter } from "../../../server/routers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getBasePlan,
  isProFamilyPlan,
  planDisplayName,
} from "@/lib/pricingPlans";
import { trpc } from "@/lib/trpc";
import { formatUsdCents } from "@/lib/usdCents";

/**
 * The confirm step before a web subscriber's plan changes. Oct 9 2026: "Get
 * Creator" charged the card on file on one click and "Get Plus" downgraded
 * at once. Now every plan button asks the server what the change would do
 * (payment.previewPlanSwitch) and shows it: the exact amount due today for
 * an upgrade, or the date a downgrade takes effect. Someone with no Stripe
 * subscription to change goes straight to Checkout as before.
 */

type PlanId =
  inferRouterInputs<AppRouter>["payment"]["previewPlanSwitch"]["planId"];
type SwitchPreview = Extract<
  inferRouterOutputs<AppRouter>["payment"]["previewPlanSwitch"],
  { mode: "switch" }
>["preview"];
type ShownPreview = Extract<SwitchPreview, { kind: "now" | "period_end" }>;

export type PlanChangeRequest = {
  /** Server plan key, e.g. "creator_monthly". */
  planId: string;
  /** Starts a new subscription's Checkout when there is none to change. */
  startCheckout: () => void;
};

const ENGLISH_PLAN_NAMES: Record<string, string> = {
  plus: "Plus",
  creator: "Creator",
  pro_v2: "Pro",
  basic: "Basic",
  standard: "Standard",
  pro: "Pro",
};

function periodOf(planId: string): "weekly" | "monthly" | "yearly" {
  if (planId.endsWith("_weekly")) return "weekly";
  if (planId.endsWith("_yearly")) return "yearly";
  return "monthly";
}

export function usePlanChangeConfirm(
  options: { source?: string; onChanged?: () => void } = {},
) {
  const { t, i18n } = useTranslation();
  const utils = trpc.useUtils();
  const [shown, setShown] = useState<{
    planId: PlanId;
    preview: ShownPreview;
  } | null>(null);
  const [requestedPlanId, setRequestedPlanId] = useState<string | null>(null);
  const previewMutation = trpc.payment.previewPlanSwitch.useMutation();
  const confirmMutation = trpc.payment.createSubscriptionCheckout.useMutation();
  const keepMutation = trpc.payment.cancelScheduledPlanChange.useMutation();

  const planLabel = (planId: string | null | undefined) => {
    if (!planId) return "";
    const base = getBasePlan(planId);
    // "Pro 22,000" for Pro's bigger credit steps.
    const name = planDisplayName(
      t,
      planId,
      ENGLISH_PLAN_NAMES[isProFamilyPlan(base) ? "pro_v2" : base] ?? base,
      i18n.language,
    );
    return t("planChange.planName", {
      plan: name,
      period: t(`planChange.${periodOf(planId)}`),
    });
  };
  const formatDate = (seconds: number) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
      new Date(seconds * 1000),
    );
  const onError = (error: { message?: string }) => {
    toast.error(error.message || t("pricing.checkoutFailed"));
  };
  const refresh = () => {
    void utils.payment.getSubscription.invalidate();
    void utils.payment.getPlanSavings.invalidate();
  };

  const request = ({ planId, startCheckout }: PlanChangeRequest) => {
    setRequestedPlanId(planId);
    previewMutation.mutate(
      { planId: planId as PlanId },
      {
        onSuccess: (data) => {
          if (data.mode === "checkout") {
            startCheckout();
            return;
          }
          const { preview } = data;
          if (preview.kind === "same_plan") {
            toast(t("planChange.alreadyOnPlan"));
            return;
          }
          posthog.capture("plan_change_confirm_shown", {
            source: options.source,
            plan: planId,
            from_plan: preview.fromPlanId,
            kind: preview.kind,
            amount_due_usd:
              preview.kind === "now" ? preview.amountDueCents / 100 : 0,
          });
          setShown({ planId: planId as PlanId, preview });
        },
        onError,
      },
    );
  };

  const dismiss = () => {
    if (!shown || confirmMutation.isPending || keepMutation.isPending) return;
    posthog.capture("plan_change_confirm_dismissed", {
      source: options.source,
      plan: shown.planId,
      kind: shown.preview.kind,
    });
    setShown(null);
  };

  const confirm = () => {
    if (!shown) return;
    const { planId, preview } = shown;
    confirmMutation.mutate(
      {
        planId,
        confirmSwitch:
          preview.kind === "now" ? { prorationDate: preview.prorationDate } : {},
      },
      {
        onSuccess: (data) => {
          refresh();
          setShown(null);
          toast.success(
            data.scheduledFor
              ? t("planChange.toastScheduled", {
                  plan: planLabel(planId),
                  date: formatDate(data.scheduledFor),
                })
              : t("planChange.toastSwitched", { plan: planLabel(planId) }),
          );
          options.onChanged?.();
        },
        onError,
      },
    );
  };

  const keepCurrent = () => {
    keepMutation.mutate(undefined, {
      onSuccess: () => {
        refresh();
        setShown(null);
        toast.success(t("planChange.toastKept"));
      },
      onError,
    });
  };

  // Rendered only while open: a closed Radix dialog still mounts its root.
  const dialog = shown ? (
    <Dialog open onOpenChange={(open) => !open && dismiss()}>
      <DialogContent className="sm:max-w-md">
        <PlanChangeBody
          preview={shown.preview}
          planId={shown.planId}
          planName={planLabel(shown.planId)}
          planLabel={planLabel}
          formatDate={formatDate}
          confirming={confirmMutation.isPending}
          keeping={keepMutation.isPending}
          onCancel={dismiss}
          onConfirm={confirm}
          onKeepCurrent={keepCurrent}
        />
      </DialogContent>
    </Dialog>
  ) : null;

  return {
    request,
    /** The plan whose preview is loading, for a spinner on its button. */
    loadingPlanId: previewMutation.isPending ? requestedPlanId : null,
    busy: previewMutation.isPending || confirmMutation.isPending,
    dialog,
  };
}

export function PlanChangeBody({
  preview,
  planId,
  planName,
  planLabel,
  formatDate,
  confirming,
  keeping,
  onCancel,
  onConfirm,
  onKeepCurrent,
}: {
  preview: ShownPreview;
  planId: string;
  planName: string;
  planLabel: (planId: string | null | undefined) => string;
  formatDate: (seconds: number) => string;
  confirming: boolean;
  keeping: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onKeepCurrent: () => void;
}) {
  const { t, i18n } = useTranslation();
  const replaces = preview.replacesPending ? (
    <p className="text-xs text-muted-foreground">
      {t("planChange.replacesNote", {
        plan: planLabel(preview.replacesPending.planId),
        date: formatDate(preview.replacesPending.effectiveAt),
      })}
    </p>
  ) : null;
  const spinner = <Loader2 className="mr-2 h-4 w-4 animate-spin" />;

  if (preview.kind === "period_end" && preview.alreadyScheduled) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>
            {t("planChange.titleScheduled", {
              plan: planName,
              date: formatDate(preview.effectiveAt),
            })}
          </DialogTitle>
          <DialogDescription>{t("planChange.scheduledNote")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={keeping}>
            {t("planChange.close")}
          </Button>
          <Button onClick={onKeepCurrent} disabled={keeping}>
            {keeping && spinner}
            {t("planChange.keepCurrent")}
          </Button>
        </DialogFooter>
      </>
    );
  }

  if (preview.kind === "period_end") {
    const per = t(
      `planChange.per${
        { weekly: "Week", monthly: "Month", yearly: "Year" }[periodOf(planId)]
      }`,
    );
    return (
      <>
        <DialogHeader>
          <DialogTitle>
            {t("planChange.titleLater", {
              plan: planName,
              date: formatDate(preview.effectiveAt),
            })}
          </DialogTitle>
          <DialogDescription>
            {t("planChange.laterNote", {
              date: formatDate(preview.effectiveAt),
              price: formatUsdCents(preview.planCents),
              per,
            })}
          </DialogDescription>
        </DialogHeader>
        {replaces}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={confirming}>
            {t("planChange.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={confirming}>
            {confirming && spinner}
            {t("planChange.confirmLater")}
          </Button>
        </DialogFooter>
      </>
    );
  }

  const rows: Array<[string, number]> = [
    [t("planChange.newPlanLine", { plan: planName }), preview.planCents],
  ];
  if (preview.unusedCreditCents > 0) {
    rows.push([t("planChange.unusedLine"), -preview.unusedCreditCents]);
  }
  if (preview.adjustmentCents !== 0) {
    rows.push([t("planChange.adjustmentsLine"), preview.adjustmentCents]);
  }
  if (preview.balanceAppliedCents > 0) {
    rows.push([t("planChange.accountCreditLine"), -preview.balanceAppliedCents]);
  }
  const signed = (cents: number) =>
    cents < 0 ? `−${formatUsdCents(-cents)}` : formatUsdCents(cents);
  const amountDue = formatUsdCents(preview.amountDueCents);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("planChange.titleNow", { plan: planName })}</DialogTitle>
        <DialogDescription className="sr-only">
          {t("planChange.dueToday")}: {amountDue}
        </DialogDescription>
      </DialogHeader>
      <dl className="space-y-1.5 text-sm">
        {rows.map(([label, cents]) => (
          <div key={label} className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="tabular-nums">{signed(cents)}</dd>
          </div>
        ))}
        <div className="flex justify-between gap-4 border-t border-border pt-2 font-semibold">
          <dt>{t("planChange.dueToday")}</dt>
          <dd className="tabular-nums">{amountDue}</dd>
        </div>
      </dl>
      <p className="text-xs text-muted-foreground">
        {t("planChange.nowRenews", { date: formatDate(preview.renewsAt) })}
        {preview.creditGrant > 0 &&
          ` ${t("planChange.nowCredits", {
            credits: preview.creditGrant.toLocaleString(i18n.language),
          })}`}
      </p>
      {replaces}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={confirming}>
          {t("planChange.cancel")}
        </Button>
        <Button onClick={onConfirm} disabled={confirming}>
          {confirming && spinner}
          {preview.amountDueCents > 0
            ? t("planChange.confirmPay", { amount: amountDue })
            : t("planChange.confirmNow")}
        </Button>
      </DialogFooter>
    </>
  );
}
